/** Certified work per project: name, next due cue, outstanding amount and a paid/outstanding bar. */
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import type { InterimApplication, Project } from '../../types/database';
import { fmtMoney } from '../../features/dashboard/utils';
import { useSharedPalette } from '../theme';
import { buildOutstandingRows, outstandingDueCue } from './homeModels';

export function OutstandingChart({ projects, applications, maxRows = 6, onOpen,pending=false,compact=false,expandable=false }: {
  pending?:boolean;
  compact?:boolean;
  expandable?:boolean;
  projects: Project[];
  applications: InterimApplication[];
  maxRows?: number;
  /** Rows are only pressable when there is somewhere to go. */
  onOpen?: (projectId: string) => void;
}) {
  const palette = useSharedPalette();
  const [expanded, setExpanded] = React.useState(false);
  const allRows = buildOutstandingRows(projects, applications, expandable ? Number.MAX_SAFE_INTEGER : maxRows);
  const rows = expandable && !expanded ? allRows.slice(0, maxRows) : allRows;
  const tone = { danger: palette.danger, warning: palette.warning, muted: palette.muted };

  if (!pending&&rows.length === 0) {
    return <Text style={[styles.empty, { color: palette.muted }]}>No certified work yet</Text>;
  }

  const scaleRows = expanded ? allRows : allRows.slice(0, Math.max(6, maxRows));
  const maxCertified = Math.max(...scaleRows.map((r) => r.certified), 1);

  return (
    <View style={[styles.wrap, compact && styles.compactWrap]}>
      {!pending&&rows.map(({ project, certified, paid, outstanding, nextDays }) => {
        const due = nextDays != null ? outstandingDueCue(nextDays) : null;
        const bar = <View style={[styles.track, compact && styles.compactTrack, {backgroundColor: palette.track}]}>
          <View style={[styles.fill, {width: `${(certified / maxCertified) * 100}%`}]}>
            <View style={{width: `${(paid / certified) * 100}%`, backgroundColor: palette.success, opacity: 0.65}}/>
            {outstanding > 0 && <View style={{width: `${(outstanding / certified) * 100}%`, backgroundColor: palette.warning, opacity: 0.8}}/>}
          </View>
        </View>;
        const content = (
          <View>
            <View style={[styles.top, compact && styles.compactTop]}>
              <View style={styles.nameBlock}>
                <Text numberOfLines={compact ? 1 : undefined} style={[styles.name, compact && styles.compactName, { color: palette.foreground }]}>{project.name}</Text>
                {!compact && due && <Text style={[styles.due, { color: tone[due.tone] }]}>{due.label}</Text>}
              </View>
              {outstanding > 0 ? (
                <Text style={[styles.amount, { color: palette.warning }]}>{fmtMoney(outstanding)}</Text>
              ) : (
                <Text style={[styles.clear, { color: palette.success }]}>clear</Text>
              )}
            </View>
            {compact ? <View style={styles.balanceRow}>
              {due && <Text style={[styles.compactDue, {color: tone[due.tone]}]}>{due.label}</Text>}
              {bar}
            </View> : bar}
          </View>
        );
        return onOpen ? (
          <Pressable key={project.id} onPress={() => onOpen(project.id)} accessibilityRole="button">
            {content}
          </Pressable>
        ) : (
          <View key={project.id}>{content}</View>
        );
      })}
      {expandable && allRows.length > maxRows && <Pressable accessibilityRole="button" accessibilityState={{expanded}} onPress={() => setExpanded(value => !value)} style={styles.more}>
        <Text style={{fontSize: 12, color: palette.muted}}>{expanded ? 'Show fewer projects' : `Show ${allRows.length - maxRows} more projects`}</Text>
      </Pressable>}
      <View style={[styles.legend, { borderTopColor: palette.border }]}>
        <Legend colour={palette.success} opacity={0.65} label="Paid" muted={palette.muted} />
        <Legend colour={palette.warning} opacity={0.8} label="Outstanding" muted={palette.muted} />
      </View>
    </View>
  );
}

function Legend({ colour, opacity, label, muted }: { colour: string; opacity: number; label: string; muted: string }) {
  return (
    <View style={styles.legendItem}>
      <View style={[styles.swatch, { backgroundColor: colour, opacity }]} />
      <Text style={[styles.legendText, { color: muted }]}>{label}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  compactWrap: {paddingVertical: 8, gap: 10},
  compactTop: {marginBottom: 6},
  compactName: {lineHeight: 18},
  balanceRow: {flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 16},
  compactDue: {fontSize: 12, lineHeight: 16, width: 78},
  compactTrack: {flex: 1, height: 8, borderRadius: 4},
  more: {minHeight: 26, justifyContent: 'center'},
  wrap: { paddingHorizontal: 16, paddingVertical: 12, gap: 14 },
  empty: { fontSize: 12, textAlign: 'center', paddingVertical: 28 },
  top: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, marginBottom: 8 },
  nameBlock: { flex: 1, minWidth: 0 },
  name: { fontSize: 14, lineHeight: 20, fontWeight: '500' },
  due: { fontSize: 12, lineHeight: 20, marginTop: 2 },
  amount: { fontSize: 14, fontWeight: '600', paddingTop: 2 },
  clear: { fontSize: 14, paddingTop: 2 },
  track: { height: 12, borderRadius: 6, overflow: 'hidden' },
  fill: { height: '100%', flexDirection: 'row', borderRadius: 6, overflow: 'hidden' },
  legend: { flexDirection: 'row', gap: 16, paddingTop: 6, borderTopWidth: StyleSheet.hairlineWidth },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  swatch: { width: 10, height: 10, borderRadius: 2 },
  legendText: { fontSize: 12 },
});
